import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { ThumbsUp, ThumbsDown, MessageSquare, Lightbulb, Bug, Filter, PlusCircle } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { useAuth } from "@/components/AuthProvider";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface FeedbackItem {
  id: string;
  type: 'feedback' | 'idea' | 'bug';
  title: string;
  description: string;
  upvotes: number;
  downvotes: number;
  created_at: string;
  user_votes?: { vote_type: boolean }[];
}

type FeedbackType = 'all' | 'feedback' | 'idea' | 'bug';

export const FeedbackList = () => {
  const { user } = useAuth();
  const [sortBy, setSortBy] = useState<'newest' | 'popular'>('newest');
  const [selectedType, setSelectedType] = useState<FeedbackType>('all');

  const { data: feedback, isLoading, refetch } = useQuery({
    queryKey: ['feedback', sortBy, selectedType],
    queryFn: async () => {
      console.log('Fetching feedback items...');
      let query = supabase
        .from('feedback')
        .select(`
          *,
          user_votes:feedback_votes(vote_type)
        `)
        .order(sortBy === 'newest' ? 'created_at' : 'upvotes', { ascending: false });

      if (selectedType !== 'all') {
        query = query.eq('type', selectedType);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching feedback:', error);
        throw error;
      }

      return data as FeedbackItem[];
    }
  });

  const handleVote = async (feedbackId: string, isUpvote: boolean) => {
    if (!user) {
      toast({
        title: "Authentication required",
        description: "Please sign in to vote",
        variant: "destructive",
      });
      return;
    }

    try {
      const { error: voteError } = await supabase
        .from('feedback_votes')
        .upsert({
          feedback_id: feedbackId,
          user_id: user.id,
          vote_type: isUpvote
        }, {
          onConflict: 'feedback_id,user_id'
        });

      if (voteError) {
        if (voteError.code === '23505') { // Unique constraint violation
          toast({
            title: "Already voted",
            description: "You have already voted on this feedback",
            variant: "destructive",
          });
          return;
        }
        throw voteError;
      }

      const { error: updateError } = await supabase
        .rpc(isUpvote ? 'increment_upvote' : 'increment_downvote', {
          row_id: feedbackId
        });

      if (updateError) throw updateError;

      refetch();
      
      toast({
        title: "Vote recorded",
        description: `Your ${isUpvote ? 'upvote' : 'downvote'} has been recorded.`,
      });
    } catch (error) {
      console.error('Error voting:', error);
      toast({
        title: "Error",
        description: "There was a problem recording your vote. Please try again.",
        variant: "destructive",
      });
    }
  };

  const getTypeIcon = (type: FeedbackItem['type']) => {
    switch (type) {
      case 'idea':
        return <Lightbulb className="w-3 h-3" />;
      case 'bug':
        return <Bug className="w-3 h-3" />;
      default:
        return <MessageSquare className="w-3 h-3" />;
    }
  };

  const getTypeStyles = (type: FeedbackItem['type']) => {
    switch (type) {
      case 'idea':
        return 'bg-amber-50 text-amber-700 border-amber-100 hover:bg-amber-100';
      case 'bug':
        return 'bg-red-50 text-red-700 border-red-100 hover:bg-red-100';
      default:
        return 'bg-blue-50 text-blue-700 border-blue-100 hover:bg-blue-100';
    }
  };

  if (isLoading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent mb-4"></div>
        <p className="text-gray-500 text-md">Loading feedback...</p>
      </div>
    );
  }

  return (
    <ScrollArea className="h-[400px] pr-4">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="text-sm bg-gray-50/80 hover:bg-gray-100/80 border-gray-200/50 rounded-xl flex items-center gap-1.5"
                >
                  <Filter className="w-3.5 h-3.5" />
                  {selectedType === 'all' ? 'All Types' : selectedType.charAt(0).toUpperCase() + selectedType.slice(1)}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-[150px] bg-white/95 backdrop-blur-sm border-gray-100 rounded-xl shadow-lg">
                <DropdownMenuItem onClick={() => setSelectedType('all')} className="hover:bg-blue-50 rounded-lg cursor-pointer">
                  <Filter className="w-4 h-4 mr-2 text-gray-500" />
                  All Types
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setSelectedType('feedback')} className="hover:bg-blue-50 rounded-lg cursor-pointer">
                  <MessageSquare className="w-4 h-4 mr-2 text-blue-500" />
                  Feedback
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setSelectedType('idea')} className="hover:bg-amber-50 rounded-lg cursor-pointer">
                  <Lightbulb className="w-4 h-4 mr-2 text-amber-500" />
                  Ideas
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setSelectedType('bug')} className="hover:bg-red-50 rounded-lg cursor-pointer">
                  <Bug className="w-4 h-4 mr-2 text-red-500" />
                  Bugs
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <div className="flex gap-2 bg-gray-50 p-1 rounded-xl">
            <Button 
              variant={sortBy === 'newest' ? 'secondary' : 'ghost'} 
              size="sm" 
              className={`text-sm rounded-lg ${sortBy === 'newest' ? 'bg-white shadow-sm' : ''}`}
              onClick={() => setSortBy('newest')}
            >
              Newest
            </Button>
            <Button 
              variant={sortBy === 'popular' ? 'secondary' : 'ghost'} 
              size="sm" 
              className={`text-sm rounded-lg ${sortBy === 'popular' ? 'bg-white shadow-sm' : ''}`}
              onClick={() => setSortBy('popular')}
            >
              Popular
            </Button>
          </div>
        </div>

        {feedback?.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-6 text-center rounded-2xl border border-gray-100 bg-gray-50/50">
            <div className="bg-white p-4 rounded-full mb-4 shadow-sm">
              <PlusCircle className="h-8 w-8 text-blue-500" />
            </div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">No feedback yet</h3>
            <p className="text-gray-500 max-w-sm">Be the first to share your thoughts and help us improve!</p>
          </div>
        ) : (
          feedback?.map((item) => (
            <div 
              key={item.id} 
              className="p-6 bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow space-y-3 group"
            >
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className={`rounded-lg ${getTypeStyles(item.type)}`}>
                    {getTypeIcon(item.type)}
                    <span className="ml-1">{item.type}</span>
                  </Badge>
                  <h3 className="font-semibold text-lg">{item.title}</h3>
                </div>
                <p className="text-gray-600">{item.description}</p>
              </div>

              <div className="flex items-center gap-4">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-green-600 hover:text-green-700 hover:bg-green-50 rounded-xl"
                  onClick={() => handleVote(item.id, true)}
                >
                  <ThumbsUp className="h-4 w-4 mr-1.5" />
                  {item.upvotes}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50 rounded-xl"
                  onClick={() => handleVote(item.id, false)}
                >
                  <ThumbsDown className="h-4 w-4 mr-1.5" />
                  {item.downvotes}
                </Button>
              </div>
            </div>
          ))
        )}
      </div>
    </ScrollArea>
  );
};
